import React, { useState, useEffect, useCallback } from 'react';
import { Globe, Building2, DollarSign, Phone, Search, Edit2, Trash2, Plus, ChevronRight, X, Check, MapPin, AlertCircle, Sparkles, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Always read token at call-time from both storages
const getToken = () =>
  localStorage.getItem('access_token') ||
  sessionStorage.getItem('access_token') ||
  (axios.defaults.headers.common['Authorization'] || '').replace('Bearer ', '');

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const TABS = [
  { id: 'countries', label: 'Countries & Cities', icon: Globe },
  { id: 'venues',    label: 'Venues',             icon: Building2 },
  { id: 'currencies',label: 'Currencies',          icon: DollarSign },
  { id: 'phonecodes',label: 'Telephone Codes',     icon: Phone },
];

// ── Shared components ──────────────────────────────────────────────────────────

const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-400"
    />
    {value && (
      <button onClick={() => onChange('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
        <X size={14} />
      </button>
    )}
  </div>
);

const Badge = ({ color = 'gray', children }) => {
  const colors = {
    green: 'bg-green-50 text-green-700',
    blue: 'bg-blue-50 text-blue-700',
    orange: 'bg-orange-50 text-orange-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[color]}`}>{children}</span>;
};

// ── Countries & Cities tab ────────────────────────────────────────────────────
function CountriesTab() {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityCounts, setCityCounts] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editCountry, setEditCountry] = useState(null);
  const [editCity, setEditCity] = useState(null);
  const [newCityName, setNewCityName] = useState('');
  const [showAddCity, setShowAddCity] = useState(false);

  const loadCountries = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const [countriesRes, countsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dropdown/countries?limit=300${q ? `&q=${q}` : ''}`),
        axios.get(`${BACKEND_URL}/api/dropdown/countries/city-counts`),
      ]);
      setCountries(countriesRes.data.items || []);
      setCityCounts(countsRes.data || {});
    } finally { setLoading(false); }
  }, []);

  const loadCities = useCallback(async (code, q = '') => {
    if (!code) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/dropdown/countries/${code}/cities${q ? `?q=${q}` : ''}`);
      setCities(res.data.items || []);
    } catch {}
  }, []);

  useEffect(() => { loadCountries(); }, [loadCountries]);

  const handleSelectCountry = (c) => {
    setSelectedCountry(c);
    setCitySearch('');
    setShowAddCity(false);
    loadCities(c.code);
  };

  useEffect(() => {
    if (selectedCountry) loadCities(selectedCountry.code, citySearch);
  }, [citySearch, selectedCountry, loadCities]);

  const handleDeleteCity = async (cityId) => {
    if (!window.confirm('Delete this city?')) return;
    await axios.delete(`${BACKEND_URL}/api/dropdown/cities/${cityId}`, { headers: authHeaders() });
    loadCities(selectedCountry.code, citySearch);
    setCityCounts(prev => ({ ...prev, [selectedCountry.code]: Math.max(0, (prev[selectedCountry.code] || 1) - 1) }));
    toast.success('City deleted');
  };

  const handleAddCity = async () => {
    if (!newCityName.trim()) return;
    await axios.post(`${BACKEND_URL}/api/dropdown/countries/${selectedCountry.code}/cities`, { name: newCityName.trim() }, { headers: authHeaders() });
    setNewCityName('');
    setShowAddCity(false);
    loadCities(selectedCountry.code);
    setCityCounts(prev => ({ ...prev, [selectedCountry.code]: (prev[selectedCountry.code] || 0) + 1 }));
    toast.success('City added');
  };

  const handleSaveCity = async (city) => {
    await axios.patch(`${BACKEND_URL}/api/dropdown/cities/${city.id}`, { name: editCity.name }, { headers: authHeaders() });
    setEditCity(null);
    loadCities(selectedCountry.code, citySearch);
    toast.success('City updated');
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Turkish locale sort for cities
  const sortedCities = [...cities].sort((a, b) =>
    a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
  );

  return (
    <div className="flex gap-4 h-full">
      {/* Left: Country List */}
      <div className="w-80 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800 text-sm">Countries <span className="text-gray-400 font-normal ml-1">({filteredCountries.length})</span></h3>
          </div>
          <SearchBar value={countrySearch} onChange={setCountrySearch} placeholder="Search countries..." />
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
          {loading && <div className="p-4 text-center text-sm text-gray-400">Loading...</div>}
          {filteredCountries.map(c => (
            <button
              key={c.code}
              onClick={() => handleSelectCountry(c)}
              className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-orange-50 transition-colors text-left ${selectedCountry?.code === c.code ? 'bg-orange-50 border-r-2 border-orange-500' : ''}`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-lg shrink-0">{c.flag}</span>
                <span className="text-sm text-gray-700 truncate">{c.name}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                {cityCounts[c.code] > 0 ? (
                  <span className="text-xs bg-blue-50 text-blue-600 font-medium px-1.5 py-0.5 rounded">
                    {cityCounts[c.code]}
                  </span>
                ) : (
                  <span className="text-xs bg-red-50 text-red-400 font-medium px-1.5 py-0.5 rounded">0</span>
                )}
                <span className="text-xs text-gray-300">{c.code}</span>
                <ChevronRight size={13} className="text-gray-300" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Cities */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
        {!selectedCountry ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <Globe size={32} className="mb-3 opacity-30" />
            <p className="text-sm">Select a country to view its cities</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{selectedCountry.flag}</span>
                  <h3 className="font-semibold text-gray-800">{selectedCountry.name}</h3>
                  <Badge color="blue">{cities.length} cities</Badge>
                  {selectedCountry.is_city_state && <Badge color="orange">City-State</Badge>}
                </div>
                <button
                  onClick={() => setShowAddCity(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 transition-colors"
                >
                  <Plus size={13} /> Add City
                </button>
              </div>
              <SearchBar value={citySearch} onChange={setCitySearch} placeholder="Search cities..." />
            </div>

            {/* Add city inline */}
            {showAddCity && (
              <div className="flex items-center gap-2 px-4 py-3 bg-orange-50 border-b border-orange-100">
                <input
                  autoFocus
                  type="text"
                  value={newCityName}
                  onChange={e => setNewCityName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCity()}
                  placeholder="New city name..."
                  className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm outline-none focus:border-orange-400"
                />
                <button onClick={handleAddCity} className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"><Check size={14} /></button>
                <button onClick={() => { setShowAddCity(false); setNewCityName(''); }} className="p-1.5 bg-gray-200 rounded hover:bg-gray-300"><X size={14} /></button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-0 divide-y divide-gray-50">
                {sortedCities.map((city) => (
                  <div key={city.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 group">
                    {editCity?.id === city.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          autoFocus
                          value={editCity.name}
                          onChange={e => setEditCity({ ...editCity, name: e.target.value })}
                          onKeyDown={e => e.key === 'Enter' && handleSaveCity(city)}
                          className="flex-1 border border-orange-300 rounded px-2 py-1 text-sm outline-none"
                        />
                        <button onClick={() => handleSaveCity(city)} className="text-green-600"><Check size={13} /></button>
                        <button onClick={() => setEditCity(null)} className="text-gray-400"><X size={13} /></button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <MapPin size={12} className="text-gray-300" />
                          <span className="text-sm text-gray-700">{city.name}</span>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setEditCity({ ...city })} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={12} /></button>
                          <button onClick={() => handleDeleteCity(city.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={12} /></button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Venues tab ────────────────────────────────────────────────────────────────
function VenuesTab() {
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [venues, setVenues] = useState([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [venueSearch, setVenueSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', address: '' });
  const [venueCounts, setVenueCounts] = useState({}); // {country_code: count}
  const [cityVenueCounts, setCityVenueCounts] = useState({}); // {cityName: count} for selected country

  // Load all countries + venue counts per country
  const loadCountries = useCallback(async () => {
    const [cRes, countsRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/dropdown/countries?limit=300`),
      axios.get(`${BACKEND_URL}/api/dropdown/venues/country-counts`),
    ]);
    setCountries(cRes.data.items || []);
    setVenueCounts(countsRes.data || {});
  }, []);

  useEffect(() => { loadCountries(); }, [loadCountries]);

  // When country selected: load its cities that have venues
  const handleSelectCountry = async (c) => {
    setSelectedCountry(c);
    setSelectedCity(null);
    setVenues([]);
    setCitySearch('');
    setShowAdd(false);
    const [citiesRes, venuesRes] = await Promise.all([
      axios.get(`${BACKEND_URL}/api/dropdown/countries/${c.code}/cities`),
      axios.get(`${BACKEND_URL}/api/dropdown/venues?country_code=${c.code}&limit=200`),
    ]);
    const allCities = citiesRes.data.items || [];
    const countryVenues = venuesRes.data.items || [];
    // Build city->venue count map
    const cvCounts = {};
    countryVenues.forEach(v => { cvCounts[v.city] = (cvCounts[v.city] || 0) + 1; });
    setCityVenueCounts(cvCounts);
    // Also include cities from venues not in city DB
    const venueCitySet = new Set(countryVenues.map(v => v.city));
    const dbCityNames = new Set(allCities.map(c => c.name));
    const extra = [...venueCitySet].filter(name => !dbCityNames.has(name)).map(name => ({ name, id: name }));
    setCities([...allCities, ...extra]);
  };

  // When city selected: load venues for that city
  const handleSelectCity = async (cityName) => {
    setSelectedCity(cityName);
    setVenueSearch('');
    setShowAdd(false);
    const res = await axios.get(`${BACKEND_URL}/api/dropdown/venues?country_code=${selectedCountry.code}&city=${encodeURIComponent(cityName)}&limit=200`);
    setVenues(res.data.items || []);
  };

  const reloadVenues = async () => {
    if (!selectedCountry || !selectedCity) return;
    const res = await axios.get(`${BACKEND_URL}/api/dropdown/venues?country_code=${selectedCountry.code}&city=${encodeURIComponent(selectedCity)}&limit=200`);
    setVenues(res.data.items || []);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this venue?')) return;
    await axios.delete(`${BACKEND_URL}/api/dropdown/venues/${id}`, { headers: authHeaders() });
    reloadVenues(); loadCountries(); toast.success('Venue deleted');
  };

  const handleSaveEdit = async (id) => {
    await axios.patch(`${BACKEND_URL}/api/dropdown/venues/${id}`, { name: editing.name, address: editing.address }, { headers: authHeaders() });
    setEditing(null); reloadVenues(); toast.success('Venue updated');
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) { toast.error('Venue name is required'); return; }
    await axios.post(`${BACKEND_URL}/api/dropdown/venues`, {
      name: addForm.name,
      country_code: selectedCountry.code,
      city: selectedCity,
      address: addForm.address,
    }, { headers: authHeaders() });
    setShowAdd(false); setAddForm({ name: '', address: '' });
    reloadVenues(); loadCountries(); toast.success('Venue added');
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );
  // Turkish locale sort
  const [aiModal, setAiModal] = useState(null); // {country, suggestions, loading, selected}

  const handleAiEnrich = async (e, country) => {
    e.stopPropagation();
    e.preventDefault();
    setAiModal({ country, suggestions: [], loading: true, selected: new Set(), error: null });
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/dropdown/venues/ai-suggest?country_code=${country.code}&country_name=${encodeURIComponent(country.name)}`
        // No auth header needed - public endpoint
      );
      setAiModal(prev => prev ? { 
        ...prev, 
        suggestions: res.data.venues || [], 
        existing: res.data.existing || [],
        loading: false 
      } : null);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Unknown error';
      setAiModal(prev => prev ? { ...prev, loading: false, error: msg } : null);
    }
  };

  const isAlreadyAdded = (suggestion, existingList) => {
    if (!existingList || !existingList.length) return false;
    const s = suggestion.name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);
    return existingList.some(e => 
      e.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20) === s
    );
  };

  const handleAiToggle = (idx) => {
    const v = aiModal.suggestions[idx];
    if (isAlreadyAdded(v, aiModal.existing)) return; // can't toggle already-added
    setAiModal(prev => {
      const s = new Set(prev.selected);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return { ...prev, selected: s };
    });
  };

  const handleAiAddSelected = async () => {
    if (!aiModal || aiModal.selected.size === 0) return;
    const toAdd = [...aiModal.selected].map(i => aiModal.suggestions[i]);
    let added = 0;
    const newlyAdded = [];
    for (const v of toAdd) {
      try {
        await axios.post(`${BACKEND_URL}/api/dropdown/venues`, {
          name: v.name, country_code: aiModal.country.code,
          city: v.city, address: v.address,
        }, { headers: authHeaders() });
        added++;
        newlyAdded.push(v.name);
      } catch { /* skip */ }
    }
    toast.success(`${added} venue added!`);
    // Update modal: clear selection, mark newly added as existing
    setAiModal(prev => prev ? {
      ...prev,
      selected: new Set(),
      existing: [...(prev.existing || []), ...newlyAdded],
    } : null);
    loadCountries();
    if (selectedCountry?.code === aiModal.country.code) handleSelectCountry(aiModal.country);
  };

  const filteredCities = cities
    .filter(c => c.name.toLowerCase().includes(citySearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
  const filteredVenues = venues.filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase()));

  return (
    <div className="flex gap-4 h-full">
      {/* Panel 1: Countries */}
      <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
        <div className="p-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 text-sm mb-2">
            Countries <span className="text-gray-400 font-normal">({filteredCountries.filter(c => venueCounts[c.code]).length})</span>
          </h3>
          <SearchBar value={countrySearch} onChange={setCountrySearch} placeholder="Search..." />
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
          {filteredCountries.filter(c => venueCounts[c.code] > 0).map(c => (
            <div key={c.code}
              className={`flex items-center justify-between px-3 py-2 hover:bg-orange-50 transition-colors group ${selectedCountry?.code === c.code ? 'bg-orange-50 border-r-2 border-orange-500' : ''}`}>
              <button onClick={() => handleSelectCountry(c)} className="flex items-center gap-2 min-w-0 flex-1 text-left">
                <span className="text-base shrink-0">{c.flag}</span>
                <span className="text-sm text-gray-700 truncate">{c.name}</span>
              </button>
              <div className="flex items-center gap-1.5 shrink-0 ml-1">
                <button
                  onClick={(e) => handleAiEnrich(e, c)}
                  title="AI ile zenginleştir"
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-purple-100 text-purple-500 hover:text-purple-700">
                  <Sparkles size={12} />
                </button>
                <span className="text-xs bg-orange-50 text-orange-600 font-medium px-1.5 py-0.5 rounded">
                  {venueCounts[c.code] || 0}
                </span>
              </div>
            </div>
          ))}
          {/* Countries with 0 venues - show them grayed with AI button */}
          {filteredCountries.filter(c => !venueCounts[c.code]).map(c => (
            <div key={c.code}
              className="flex items-center justify-between px-3 py-2 hover:bg-orange-50 transition-colors group">
              <button onClick={() => handleSelectCountry(c)} className="flex items-center gap-2 min-w-0 flex-1 text-left opacity-50">
                <span className="text-base shrink-0">{c.flag}</span>
                <span className="text-sm text-gray-500 truncate">{c.name}</span>
              </button>
              <button
                onClick={(e) => handleAiEnrich(e, c)}
                title="AI ile ekle"
                className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-600 hover:bg-purple-100 shrink-0">
                <Sparkles size={10} /> AI
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Panel 2: Cities */}
      <div className="w-52 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
        {!selectedCountry ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-4">
            <Building2 size={28} className="mb-2 opacity-30" />
            <p className="text-xs text-center">Select a country</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <span>{selectedCountry.flag}</span>
                <h3 className="font-semibold text-gray-800 text-sm truncate">{selectedCountry.name}</h3>
              </div>
              <SearchBar value={citySearch} onChange={setCitySearch} placeholder="Search cities..." />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {filteredCities.map(c => {
                const cnt = cityVenueCounts[c.name] || 0;
                return (
                  <button key={c.id || c.name} onClick={() => handleSelectCity(c.name)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-orange-50 transition-colors text-left ${selectedCity === c.name ? 'bg-orange-50 border-r-2 border-orange-500' : ''}`}>
                    <span className="text-sm text-gray-700 truncate">{c.name}</span>
                    <div className="flex items-center gap-1 shrink-0 ml-1">
                      {cnt > 0 ? (
                        <span className="text-xs bg-orange-50 text-orange-500 font-medium px-1.5 py-0.5 rounded">{cnt}</span>
                      ) : (
                        <ChevronRight size={13} className="text-gray-300" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Panel 3: Venues */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
        {!selectedCity ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MapPin size={32} className="mb-3 opacity-30" />
            <p className="text-sm">Select a city to view its venues</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span>{selectedCountry.flag}</span>
                  <span className="font-semibold text-gray-800">{selectedCity}</span>
                  <Badge color="orange">{filteredVenues.length} venues</Badge>
                </div>
                <button onClick={() => setShowAdd(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 transition-colors">
                  <Plus size={13} /> Add Venue
                </button>
              </div>
              <SearchBar value={venueSearch} onChange={setVenueSearch} placeholder="Search venues..." />
            </div>

            {showAdd && (
              <div className="p-4 bg-orange-50 border-b border-orange-100 space-y-2">
                <input className="w-full border border-gray-200 rounded px-3 py-2 text-sm outline-none focus:border-orange-400"
                  placeholder="Venue name *" value={addForm.name} onChange={e => setAddForm(f => ({...f, name: e.target.value}))} autoFocus />
                <input className="w-full border border-gray-200 rounded px-3 py-2 text-sm outline-none focus:border-orange-400"
                  placeholder="Address (optional)" value={addForm.address} onChange={e => setAddForm(f => ({...f, address: e.target.value}))} />
                <div className="flex gap-2">
                  <button onClick={handleAdd} className="px-4 py-1.5 bg-green-500 text-white rounded text-sm hover:bg-green-600">Add</button>
                  <button onClick={() => setShowAdd(false)} className="px-4 py-1.5 bg-gray-200 rounded text-sm hover:bg-gray-300">Cancel</button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
              {filteredVenues.map(v => (
                <div key={v.id} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50 group">
                  {editing?.id === v.id ? (
                    <div className="flex-1 space-y-2">
                      <input className="w-full border border-orange-300 rounded px-2 py-1.5 text-sm outline-none" value={editing.name} onChange={e => setEditing(p => ({...p, name: e.target.value}))} />
                      <input className="w-full border border-orange-300 rounded px-2 py-1.5 text-sm outline-none" value={editing.address || ''} onChange={e => setEditing(p => ({...p, address: e.target.value}))} placeholder="Address" />
                      <div className="flex gap-1">
                        <button onClick={() => handleSaveEdit(v.id)} className="text-green-600 p-1"><Check size={13} /></button>
                        <button onClick={() => setEditing(null)} className="text-gray-400 p-1"><X size={13} /></button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <MapPin size={12} className="text-orange-400 shrink-0" />
                          <span className="text-sm font-medium text-gray-800">{v.name}</span>
                        </div>
                        {v.address && <p className="text-xs text-gray-400 mt-0.5 ml-5 truncate">{v.address}</p>}
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0">
                        <button onClick={() => setEditing({...v})} className="p-1 text-gray-400 hover:text-blue-500"><Edit2 size={12} /></button>
                        <button onClick={() => handleDelete(v.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={12} /></button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {filteredVenues.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <MapPin size={24} className="mb-2 opacity-30" />
                  <p className="text-sm">No venues in {selectedCity}</p>
                  <button onClick={() => setShowAdd(true)} className="mt-3 text-xs text-orange-500 hover:underline">+ Add first venue</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* AI Enrich Modal */}
      {aiModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Sparkles size={16} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">AI Venue Suggestions</h3>
                  <p className="text-xs text-gray-500">{aiModal.country.flag} {aiModal.country.name} — select venues to add</p>
                </div>
              </div>
              <button onClick={() => setAiModal(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={16} /></button>
            </div>

            <div className="p-5">
              {aiModal.loading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader size={28} className="text-purple-500 animate-spin" />
                  <p className="text-sm text-gray-500">AI is searching for venues in {aiModal.country.name}...</p>
                </div>
              ) : aiModal.error ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-red-500">
                  <AlertCircle size={28} />
                  <p className="text-sm font-medium">Error</p>
                  <p className="text-xs text-center text-gray-500 max-w-xs">{aiModal.error}</p>
                  <button onClick={() => handleAiEnrich({ stopPropagation: ()=>{}, preventDefault: ()=>{} }, aiModal.country)}
                    className="mt-2 px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">
                    Try Again
                  </button>
                </div>
              ) : aiModal.suggestions.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <MapPin size={28} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No suggestions found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {aiModal.suggestions.map((v, idx) => {
                    const added = isAlreadyAdded(v, aiModal.existing);
                    const selected = aiModal.selected.has(idx);
                    return (
                      <label key={idx}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                          added
                            ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                            : selected
                              ? 'border-purple-400 bg-purple-50 cursor-pointer'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={added}
                          onChange={() => handleAiToggle(idx)}
                          className="mt-0.5 accent-purple-600"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`font-semibold text-sm ${added ? 'text-gray-400' : 'text-gray-800'}`}>{v.name}</p>
                            {added && (
                              <span className="flex items-center gap-1 text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full font-medium shrink-0">
                                <Check size={10} /> Added
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">
                            <span className={added ? 'text-gray-400' : 'font-medium text-gray-500'}>{v.city}</span>
                            {v.address && <span> — {v.address}</span>}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {!aiModal.loading && aiModal.suggestions.length > 0 && (
              <div className="px-5 pb-5 flex items-center justify-between border-t border-gray-100 pt-4">
                <button
                  onClick={() => setAiModal(prev => {
                    const selectableIdxs = prev.suggestions
                      .map((v, i) => i)
                      .filter(i => !isAlreadyAdded(prev.suggestions[i], prev.existing));
                    const allSelected = selectableIdxs.every(i => prev.selected.has(i));
                    return { ...prev, selected: allSelected ? new Set() : new Set(selectableIdxs) };
                  })}
                  className="text-xs text-purple-600 hover:underline">
                  {aiModal.suggestions.filter((v, i) => !isAlreadyAdded(v, aiModal.existing) && aiModal.selected.has(i)).length ===
                   aiModal.suggestions.filter((v) => !isAlreadyAdded(v, aiModal.existing)).length
                    ? 'Deselect all' : 'Select all'}
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setAiModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                  <button onClick={handleAiAddSelected} disabled={aiModal.selected.size === 0}
                    className="px-5 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-40 flex items-center gap-2 transition-all"
                    style={{ background: aiModal.selected.size === 0 ? '#C4B5FD' : 'linear-gradient(135deg,#7C3AED,#9333EA)' }}>
                    <Plus size={14} /> Add {aiModal.selected.size > 0 ? `${aiModal.selected.size} venue${aiModal.selected.size > 1 ? 's' : ''}` : 'Selected'}
                  </button>                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Currencies tab ────────────────────────────────────────────────────────────
function CurrenciesTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/dropdown/currencies${search ? `?q=${search}` : ''}`);
    setItems(res.data.items || []);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (code) => {
    await axios.patch(`${BACKEND_URL}/api/dropdown/currencies/${code}`, editing, { headers: authHeaders() });
    setEditing(null); load(); toast.success('Currency updated');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Currencies <span className="text-gray-400 font-normal ml-1">({items.length})</span></h3>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by country or currency..." />
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Country</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Currency</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Code</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Symbol</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map(c => (
              <tr key={c.code} className="hover:bg-gray-50 group">
                {editing?.code === c.code ? (
                  <>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-1.5"><span className="text-lg">{c.flag}</span><span>{c.name}</span></span></td>
                    <td className="px-4 py-2"><input className="w-full border border-orange-300 rounded px-2 py-1 text-sm outline-none" value={editing.currency_name} onChange={e => setEditing(p => ({...p, currency_name: e.target.value}))} /></td>
                    <td className="px-4 py-2"><input className="w-20 border border-orange-300 rounded px-2 py-1 text-sm outline-none" value={editing.currency_code} onChange={e => setEditing(p => ({...p, currency_code: e.target.value}))} /></td>
                    <td className="px-4 py-2"><input className="w-16 border border-orange-300 rounded px-2 py-1 text-sm outline-none" value={editing.currency_symbol} onChange={e => setEditing(p => ({...p, currency_symbol: e.target.value}))} /></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => handleSave(c.code)} className="text-green-600 p-1"><Check size={13} /></button>
                        <button onClick={() => setEditing(null)} className="text-gray-400 p-1"><X size={13} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5"><span className="flex items-center gap-1.5"><span className="text-lg">{c.flag}</span><span className="text-gray-700">{c.name}</span></span></td>
                    <td className="px-4 py-2.5 text-gray-700">{c.currency_name}</td>
                    <td className="px-4 py-2.5"><Badge color="blue">{c.currency_code}</Badge></td>
                    <td className="px-4 py-2.5 font-mono text-gray-600">{c.currency_symbol}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => setEditing({...c})} className="p-1 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100"><Edit2 size={13} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Phone Codes tab ───────────────────────────────────────────────────────────
function PhoneCodesTab() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/dropdown/phone-codes${search ? `?q=${search}` : ''}`);
    setItems(res.data.items || []);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (code) => {
    await axios.patch(`${BACKEND_URL}/api/dropdown/phone-codes/${code}`, editing, { headers: authHeaders() });
    setEditing(null); load(); toast.success('Phone code updated');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Telephone Codes <span className="text-gray-400 font-normal ml-1">({items.length})</span></h3>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle size={13} className="text-amber-600" />
            <span className="text-xs text-amber-700">Phone fields = Code dropdown + max 12-digit number field</span>
          </div>
        </div>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by country or code..." />
      </div>
      <div className="flex-1 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Country</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Dial Code</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map(c => (
              <tr key={c.code} className="hover:bg-gray-50 group">
                <td className="px-4 py-2.5"><span className="flex items-center gap-1.5"><span className="text-lg">{c.flag}</span><span className="text-gray-700">{c.name}</span></span></td>
                {editing?.code === c.code ? (
                  <>
                    <td className="px-4 py-2"><input className="w-32 border border-orange-300 rounded px-2 py-1 text-sm outline-none font-mono" value={editing.phone_code} onChange={e => setEditing(p => ({...p, phone_code: e.target.value}))} /></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => handleSave(c.code)} className="text-green-600 p-1"><Check size={13} /></button>
                        <button onClick={() => setEditing(null)} className="text-gray-400 p-1"><X size={13} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5"><span className="font-mono font-semibold text-gray-800">{c.phone_code}</span></td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => setEditing({...c})} className="p-1 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100"><Edit2 size={13} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function UltraDropdowns() {
  const [activeTab, setActiveTab] = useState('countries');

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dropdown Management</h1>
        <p className="text-gray-500 text-sm mt-1">Single source of truth for all dropdown data used across the platform</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0">
        {activeTab === 'countries'  && <CountriesTab />}
        {activeTab === 'venues'     && <VenuesTab />}
        {activeTab === 'currencies' && <CurrenciesTab />}
        {activeTab === 'phonecodes' && <PhoneCodesTab />}
      </div>
    </div>
  );
}
